/**
 * Operational Fees Panel — Tax Search Team Leader restricted view.
 *
 * May 17, 2026 — RBAC enhancement.
 *
 * Replaces the full Billing Information block (`OrderBillingPanel`) on
 * the Overview tab for users with role `tax_search_team_leader`.
 *
 * INTENTIONALLY does NOT render any of:
 *   * Base Price          * Margin
 *   * Internal Cost       * Total Billable
 *   * Override controls   * Recalculate controls
 *   * Client pricing badge
 *
 * The component is structurally separate (not a flag toggle on
 * OrderBillingPanel) so there is zero possible code path through which
 * sensitive billing fields can leak into the Tax Team Leader DOM.
 *
 * Allowed actions:
 *   * Add Paid Site Fee (amount + description)
 *   * Edit a Paid Site Fee they (or admin/accounting) previously added
 *
 * Lock behavior:
 *   * Read-only when `billing_status ∈ ('invoiced','paid')` or
 *     `billing_locked === true`.
 *
 * Accounting review flag:
 *   * Adds/edits > $50 set `audit_required=true` on the order. Banner
 *     surfaces this so the Tax Team Leader knows Accounting will review.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Plus, Edit, X, Save, Lock, AlertTriangle, Trash2, Coins, FileText } from 'lucide-react';
import './order-overview-premium.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { formatDateTimeLocal } from '../../utils/formatters';

// TITLE-FEE-TYPES-001 (Jun 2026) — predefined Operational Fee types for
// TITLE SEARCH products only ("Other" keeps free-text for anything else).
// OPS-FEE-TYPES-002 (Oct 2026): + Additional Copy Fee, Additional Search Fee, Ecclix.
// Single source of truth — the backend stores the label as `description` (no server-side list).
export const TITLE_FEE_TYPES = ['Pacer', 'Fidler', 'LandEx', 'Landshark', 'Additional Copy Fee', 'Additional Search Fee', 'Ecclix'];

// OPS-FEE-TREATMENT-001 — who pays this fee. Legacy rows (no value) are billed.
const TREATMENTS = [
  { value: 'internal_cost', label: 'Internal Cost', hint: 'not billed to client', badge: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'client_pass_through', label: 'Client Pass-Through', hint: 'added to client invoice', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];
const effectiveTreatment = (fee) => (fee?.billing_treatment === 'internal_cost' ? 'internal_cost' : 'client_pass_through');
const isLegacyTreatment = (fee) => !TREATMENTS.some(t => t.value === fee?.billing_treatment);

const TreatmentBadge = ({ fee }) => {
  const t = TREATMENTS.find(x => x.value === effectiveTreatment(fee));
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-medium whitespace-nowrap ${t.badge}`}
      title={isLegacyTreatment(fee) ? 'Legacy fee — billing treatment not set; billed to client (backward compatible)' : `${t.label} — ${t.hint}`}
      data-testid={`ops-fee-treatment-badge-${fee.id}`}
    >
      {t.label}{isLegacyTreatment(fee) ? ' · legacy' : ''}
    </span>
  );
};

const TreatmentPicker = ({ value, onChange, baseBilling, amount, testid, disabled }) => {
  const amt = Number(amount) || 0;
  const base = Number(baseBilling) || 0;
  return (
    <div className="space-y-1" data-testid={testid}>
      <Label className="text-xs">Billing Treatment <span className="text-red-500">*</span></Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {TREATMENTS.map(t => (
          <button
            type="button"
            key={t.value}
            disabled={disabled}
            onClick={() => onChange(t.value)}
            data-testid={`${testid}-${t.value}`}
            className={`text-left rounded-md border px-2.5 py-1.5 transition-colors ${value === t.value ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-300' : 'border-slate-200 bg-white hover:bg-slate-50'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <span className="block text-xs font-semibold text-slate-800">{t.label}</span>
            <span className="block text-[11px] text-slate-500">{t.hint}</span>
          </button>
        ))}
      </div>
      {value && amt > 0 && (
        <p className="text-[11px] text-slate-600" data-testid={`${testid}-effect`}>
          {baseBilling == null
            // FIN-VIS-001: customer billing figures are confidential — shown only when the server exposed the contracted rate
            ? (value === 'internal_cost' ? `Recorded as internal operational cost (${fmtUSD(amt)}); customer billing unchanged.` : `Passed through to the customer invoice (${fmtUSD(amt)}).`)
            : value === 'internal_cost'
            ? `Customer billing stays ${fmtUSD(base)} · recorded as internal operational cost (${fmtUSD(amt)}).`
            : `Customer billing becomes ${fmtUSD(base + amt)} (${fmtUSD(base)} + ${fmtUSD(amt)}).`}
        </p>
      )}
    </div>
  );
};
const feeTypeFromDescription = (desc) => {
  const match = TITLE_FEE_TYPES.find(t => t.toLowerCase() === String(desc || '').trim().toLowerCase());
  return match || 'Other';
};

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const fmtUSD = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return formatDateTimeLocal(iso); } catch { return iso; }
};
// TITLE-FEE-UI-001 — compact ledger date, e.g. "Sep 1, 10:39 AM"
const fmtDateShort = (iso) => {
  if (!iso) return '';
  try {
    return formatDateTimeLocal(iso);
  } catch { return iso; }
};

export const OperationalFeesPanel = ({ order, userRole, onUpdate, currentUserId }) => {
  // Hard gate — render nothing if the role isn't permitted. Defensive even
  // though the OrderTabs mount-site already restricts. (Gate applied after
  // hook declarations for rules-of-hooks compliance.)
  // TITLE-OPS-FEES-001 (Aug 2026): Title Search TL + Processors record
  // operational costs on Title Search orders (backend scopes per-order).
  // Processors can add/view but not edit existing entries.
  // TITLE-QC-DOC-EXPENSE-001 (Oct 2026): QC Specialist (title scope) records
  // document-purchase expenses: add / view / edit OWN entries, no removal.
  const allowedRoles = [
    'admin', 'accounting', 'tax_search_team_leader',
    'title_search_team_leader', 'title_search_processor', 'search_processor',
    'typing_processor', 'qc_specialist',
  ];
  const normalizedRole = (userRole || '').toLowerCase();
  const hasAccess = allowedRoles.includes(normalizedRole);
  // TITLE-FEE-TYPES-002: Typing views fees strictly READ-ONLY (no add/edit).
  const isReadOnly = normalizedRole === 'typing_processor';
  const isQc = normalizedRole === 'qc_specialist';
  const canEdit = !isReadOnly && !['title_search_processor', 'search_processor'].includes(normalizedRole);
  const canEditFee = (fee) => canEdit && (!isQc || (currentUserId && fee.added_by === currentUserId));
  const canDelete = !isReadOnly && !isQc;

  const [fees, setFees] = useState([]);
  const [auditThreshold, setAuditThreshold] = useState(50);
  const [billingLocked, setBillingLocked] = useState(false);
  const [auditRequired, setAuditRequired] = useState(false);
  const [feeWorkflow, setFeeWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);

  // Add-form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [addDescription, setAddDescription] = useState('');
  const [addFeeType, setAddFeeType] = useState('');
  const [addTreatment, setAddTreatment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // DUP-SUBMIT-001 — one key per opened Add form; reused on retry so the server
  // can recognise a re-submission of the same intended action.
  const [addIdemKey, setAddIdemKey] = useState('');
  const newIdemKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  // Edit-form state (per-row)
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editFeeType, setEditFeeType] = useState('');
  const [editTreatment, setEditTreatment] = useState('');
  const [canChangeTreatment, setCanChangeTreatment] = useState(false);
  const [billableTotal, setBillableTotal] = useState(0);
  const [internalTotal, setInternalTotal] = useState(0);

  // TITLE-FEE-TYPES-003 — delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // TITLE-FEE-TYPES-002: classification comes from the backend
  // (_paid_site_fee_order_workflow via GET fee_workflow) — single source of
  // truth. Until that response lands (or if it fails), fall back to the SAME
  // keyword rule computed synchronously from client_product so the dropdown
  // never regresses to the plain Description input on title orders.
  const clientProductWorkflow = ['tax', 'mls', 'utility', 'municipal', 'hoa']
    .some(k => String(order?.client_product || '').toLowerCase().includes(k)) ? 'tax' : 'title';
  const isTitleSearch = (feeWorkflow || clientProductWorkflow) === 'title';

  const loadFees = async () => {
    if (!order?.id) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/pricing/orders/${order.id}/paid-site-fees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFees(res.data.paid_site_fees || []);
      setAuditThreshold(res.data.audit_threshold ?? 50);
      setBillingLocked(!!res.data.billing_locked);
      setAuditRequired(!!res.data.audit_required);
      setFeeWorkflow(res.data.fee_workflow || null);
      setCanChangeTreatment(!!res.data.can_change_treatment);
      setBillableTotal(Number(res.data.total_paid_site_fees_billable) || 0);
      setInternalTotal(Number(res.data.total_paid_site_fees_internal) || 0);
    } catch (e) {
      toast.error('Failed to load Paid Site Fees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (hasAccess) loadFees(); /* eslint-disable-next-line */ }, [order?.id, hasAccess]);

  if (!hasAccess) {
    return null;
  }

  const submitAdd = async () => {
    const amt = parseFloat(addAmount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    let description = addDescription.trim();
    if (isTitleSearch) {
      if (!addFeeType) { toast.error('Select a fee type'); return; }
      if (addFeeType !== 'Other') {
        description = addFeeType;
      } else if (description.length < 3) {
        toast.error('Description is required (3+ characters)'); return;
      }
    } else if (description.length < 3) {
      toast.error('Description is required (3+ characters)'); return;
    }
    if (!addTreatment) { toast.error('Select a Billing Treatment (Internal Cost or Client Pass-Through)'); return; }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/pricing/orders/${order.id}/paid-site-fees`,
        { amount: amt, description, billing_treatment: addTreatment },
        { headers: { Authorization: `Bearer ${token}`, 'Idempotency-Key': addIdemKey || newIdemKey() } },
      );
      if (res.data.duplicate_suppressed) {
        toast.info(`This ${fmtUSD(amt)} fee was already recorded — no duplicate added.`, { id: 'dup-fee' });
      } else {
        toast.success(
          res.data.threshold_crossed
            ? `Paid Site Fee added (${fmtUSD(amt)}). Accounting review flagged.`
            : `Paid Site Fee added (${fmtUSD(amt)}) — ${addTreatment === 'internal_cost' ? 'Internal Cost, not billed' : 'Client Pass-Through, billed'}.`
        );
      }
      setShowAddForm(false);
      setAddAmount('');
      setAddDescription('');
      setAddFeeType('');
      setAddTreatment('');
      setAddIdemKey('');
      await loadFees();
      if (onUpdate) onUpdate();
    } catch (e) {
      // DUP-SUBMIT-001: a lost response looks like an error — refresh first and
      // check whether the fee actually landed before inviting a retry.
      try {
        const token = localStorage.getItem('token');
        const fresh = await axios.get(`${API}/pricing/orders/${order.id}/paid-site-fees`, { headers: { Authorization: `Bearer ${token}` } });
        const list = fresh.data.paid_site_fees || [];
        setFees(list);
        const cutoff = Date.now() - 2 * 60 * 1000;
        const landed = list.find((c) => Math.abs(Number(c.amount) - amt) < 0.005
          && (c.description || '').trim().toLowerCase() === description.toLowerCase()
          && new Date(c.added_at).getTime() >= cutoff);
        if (landed) {
          toast.info(`Fee ${fmtUSD(amt)} was already recorded — list refreshed, no retry needed.`, { id: 'dup-fee' });
          setShowAddForm(false); setAddAmount(''); setAddDescription(''); setAddFeeType(''); setAddTreatment(''); setAddIdemKey('');
          if (onUpdate) onUpdate();
          return;
        }
      } catch { /* fall through to the real error */ }
      const detail = e?.response?.data?.detail || 'Failed to add Paid Site Fee';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (fee) => {
    setEditingId(fee.id);
    setEditAmount(String(fee.amount ?? ''));
    setEditDescription(fee.description || '');
    setEditFeeType(isTitleSearch ? feeTypeFromDescription(fee.description) : '');
    setEditTreatment(effectiveTreatment(fee));
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditAmount('');
    setEditDescription('');
    setEditFeeType('');
    setEditTreatment('');
  };

  const submitEdit = async (charge_id) => {
    const amt = parseFloat(editAmount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    let description = editDescription.trim();
    if (isTitleSearch) {
      if (!editFeeType) { toast.error('Select a fee type'); return; }
      if (editFeeType !== 'Other') {
        description = editFeeType;
      } else if (description.length < 3) {
        toast.error('Description is required (3+ characters)'); return;
      }
    } else if (description.length < 3) {
      toast.error('Description is required (3+ characters)'); return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const body = { amount: amt, description };
      if (canChangeTreatment && editTreatment) body.billing_treatment = editTreatment;
      const res = await axios.patch(
        `${API}/pricing/orders/${order.id}/paid-site-fees/${charge_id}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        res.data.threshold_crossed
          ? `Paid Site Fee updated. Accounting review flagged.`
          : `Paid Site Fee updated.`
      );
      cancelEdit();
      await loadFees();
      if (onUpdate) onUpdate();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to update Paid Site Fee';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const total = fees.reduce((sum, f) => sum + Number(f.amount || 0), 0);
  // Live customer-billing effect: contracted base = rate × parcels (+ other billable fees already on the order)
  const baseBilling = (() => {
    // FIN-VIS-001: rate fields are redacted server-side for users without "View Search Pricing & Margin" → null (no figure shown)
    if (order?.billing_override_price == null && order?.billing_unit_price_snapshot == null) return null;
    const rate = Number(order?.billing_override_price || order?.billing_unit_price_snapshot || 0);
    const qty = Math.max(parseInt(order?.parcel_count || 1, 10) || 1, 1);
    return rate * qty + billableTotal;
  })();

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/pricing/orders/${order.id}/paid-site-fees/${deleteTarget.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setFees(prev => prev.filter(f => f.id !== deleteTarget.id));
      toast.success(`Operational Fee removed (${fmtUSD(deleteTarget.amount)}). Recorded in audit history.`);
      setDeleteTarget(null);
      if (onUpdate) onUpdate();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to remove Operational Fee';
      toast.error(detail);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="ov-card ov-premium px-[18px] pb-[14px]"
      data-accent="violet"
      data-testid="operational-fees-panel"
    >
      <div className="ov-card-head !px-0">
        <div className="flex items-center gap-3">
          <span className="ov-card-ic"><Coins /></span>
          <div>
            <h3 className="ov-card-title">Operational Fees</h3>
            <p className="ov-card-sub">Paid Site Fees recorded against this order</p>
          </div>
        </div>
        {billingLocked && (
          <span className="ov-chip" data-tone="locked" data-testid="ops-fees-locked-badge">
            <Lock className="w-3.5 h-3.5" /> Locked (Invoiced)
          </span>
        )}
      </div>

      {auditRequired && (
        <div
          className="flex items-start gap-2 mb-3 px-3 py-2 rounded-md bg-orange-100 border border-orange-300"
          data-testid="ops-fees-audit-banner"
        >
          <AlertTriangle className="w-4 h-4 text-orange-700 mt-0.5" />
          <p className="text-xs text-orange-800">
            This order is flagged for Accounting review. A Paid Site Fee above {fmtUSD(auditThreshold)} triggers the review.
          </p>
        </div>
      )}

      {/* Fee ledger — TITLE-FEE-UI-001 compact table layout */}
      <div className={fees.length > 0 ? 'ov-list mb-2.5' : 'mb-2.5'} data-testid="ops-fees-list">
        {loading && <p className="text-sm text-slate-500 px-3 py-2">Loading…</p>}
        {!loading && fees.length === 0 && (
          <div className="ov-empty" data-testid="ops-fees-empty">
            <div className="flex items-center gap-3 min-w-0">
              <span className="ov-empty-ic"><FileText /></span>
              <div>
                <p className="ov-empty-title">No Paid Site Fees yet.</p>
                <p className="ov-empty-sub">Add any paid site fees that apply to this order.</p>
              </div>
            </div>
            {!billingLocked && !isReadOnly && !showAddForm && (
              <button type="button" className="ov-btn" data-variant="violet" onClick={() => { setShowAddForm(true); setAddIdemKey(newIdemKey()); }} data-testid="ops-fees-add-button">
                <Plus /> Add Paid Site Fee
              </button>
            )}
          </div>
        )}
        {!loading && fees.length > 0 && (
          <div
            className="hidden sm:grid grid-cols-[1.1fr_0.6fr_1.1fr_1fr_64px] gap-2 items-center px-3 py-2 bg-slate-50 border-b border-slate-100"
            data-testid="ops-fees-header-row"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Fee Type</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-right">Amount</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Added By</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Date/Time</span>
            <span />
          </div>
        )}
        {!loading && fees.map((fee) => {
          const isEditing = editingId === fee.id;
          return (
            <div
              key={fee.id}
              className="border-b border-slate-100 last:border-b-0"
              data-testid={`ops-fee-row-${fee.id}`}
            >
              {!isEditing ? (
                <div className="grid grid-cols-[1fr_64px] sm:grid-cols-[1.1fr_0.6fr_1.1fr_1fr_64px] gap-2 items-center px-3 py-1.5 hover:bg-slate-50/70 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {fee.description}
                      <span className="ml-1.5 align-middle"><TreatmentBadge fee={fee} /></span>
                      {fee.last_edited_at && (
                        <span className="ml-1 text-[10px] text-slate-400 font-normal" title={`Edited by ${fee.last_edited_by_name || 'Unknown'} on ${fmtDate(fee.last_edited_at)}`}>
                          (edited)
                        </span>
                      )}
                    </p>
                    <p className="sm:hidden text-[11px] text-slate-400 truncate">
                      {fmtUSD(fee.amount)} · {fee.added_by_name || 'Unknown'} · {fmtDateShort(fee.added_at)}
                    </p>
                  </div>
                  <span className="hidden sm:block text-sm font-semibold text-slate-900 text-right tabular-nums">
                    {fmtUSD(fee.amount)}
                  </span>
                  <span className="hidden sm:block text-xs text-slate-500 truncate">
                    {fee.added_by_name || 'Unknown'}
                  </span>
                  <span className="hidden sm:block text-xs text-slate-400" title={fmtDate(fee.added_at)}>
                    {fmtDateShort(fee.added_at)}
                  </span>
                  <div className="flex gap-0.5 justify-end">
                    {!billingLocked && !isReadOnly && (
                      <>
                        {canEditFee(fee) && (
                          <button
                            type="button"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                            onClick={() => startEdit(fee)}
                            title="Edit fee"
                            data-testid={`ops-fee-edit-${fee.id}`}
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDelete && (
                        <button
                          type="button"
                          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          onClick={() => setDeleteTarget(fee)}
                          title="Remove fee"
                          data-testid={`ops-fee-delete-${fee.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2 px-3 py-2 bg-slate-50/60">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Amount (USD)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editAmount}
                        onChange={(e) => setEditAmount(e.target.value)}
                        data-testid={`ops-fee-edit-amount-${fee.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{isTitleSearch ? 'Fee Type' : 'Description'}</Label>
                      {isTitleSearch ? (
                        <div className="space-y-1.5">
                          <Select value={editFeeType || undefined} onValueChange={setEditFeeType}>
                            <SelectTrigger data-testid={`ops-fee-edit-type-${fee.id}`}>
                              <SelectValue placeholder="Select fee type" />
                            </SelectTrigger>
                            <SelectContent>
                              {TITLE_FEE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          {editFeeType === 'Other' && (
                            <Input
                              placeholder="Describe the expense"
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              data-testid={`ops-fee-edit-desc-${fee.id}`}
                            />
                          )}
                        </div>
                      ) : (
                        <Input
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          data-testid={`ops-fee-edit-desc-${fee.id}`}
                        />
                      )}
                    </div>
                  </div>
                  <TreatmentPicker
                    value={editTreatment}
                    onChange={setEditTreatment}
                    baseBilling={baseBilling == null ? null : baseBilling - (effectiveTreatment(fee) === 'client_pass_through' ? Number(fee.amount || 0) : 0)}
                    amount={editAmount}
                    disabled={!canChangeTreatment}
                    testid={`ops-fee-edit-treatment-${fee.id}`}
                  />
                  {!canChangeTreatment && (
                    <p className="text-[11px] text-slate-500" data-testid={`ops-fee-edit-treatment-locked-${fee.id}`}>
                      Billing Treatment of a saved fee can only be changed by Admin or Accounting.
                    </p>
                  )}
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={cancelEdit} disabled={submitting}>
                      <X className="w-3 h-3 mr-1" /> Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => submitEdit(fee.id)}
                      disabled={submitting}
                      data-testid={`ops-fee-edit-save-${fee.id}`}
                    >
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Summary row — visually distinct total at the bottom of the ledger */}
        {!loading && fees.length > 0 && (
          <div
            className="flex justify-between items-center px-3 py-2.5 bg-slate-50 border-t border-slate-200"
            data-testid="ops-fees-total"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Total Operational Fees
              <span className="block sm:inline sm:ml-3 normal-case tracking-normal font-normal text-slate-500" data-testid="ops-fees-split">
                Billed to client <span className="font-semibold text-emerald-700" data-testid="ops-fees-billable-total">{fmtUSD(billableTotal)}</span>
                {' · '}Internal (not billed) <span className="font-semibold text-slate-700" data-testid="ops-fees-internal-total">{fmtUSD(internalTotal)}</span>
              </span>
            </div>
            <span className="text-sm font-bold text-slate-900 tabular-nums">{fmtUSD(total)}</span>
          </div>
        )}
      </div>

      {/* Add form — hidden entirely for read-only (Typing) viewers */}
      {!billingLocked && !isReadOnly && (
        <div data-testid="ops-fees-add-block">
          {!showAddForm ? (
            fees.length > 0 && (
              <div className="flex justify-end">
                <button type="button" className="ov-btn" data-variant="violet" onClick={() => { setShowAddForm(true); setAddIdemKey(newIdemKey()); }} data-testid="ops-fees-add-button">
                  <Plus /> Add Paid Site Fee
                </button>
              </div>
            )
          ) : (
            <div className="ov-form space-y-2 ov-enter" data-testid="ops-fees-add-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Amount (USD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={addAmount}
                    onChange={(e) => setAddAmount(e.target.value)}
                    data-testid="ops-fees-add-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs">{isTitleSearch ? 'Fee Type' : 'Description'}</Label>
                  {isTitleSearch ? (
                    <div className="space-y-1.5">
                      <Select value={addFeeType || undefined} onValueChange={setAddFeeType}>
                        <SelectTrigger data-testid="ops-fees-add-type">
                          <SelectValue placeholder="Select fee type" />
                        </SelectTrigger>
                        <SelectContent>
                          {TITLE_FEE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {addFeeType === 'Other' && (
                        <Input
                          placeholder="Describe the expense"
                          value={addDescription}
                          onChange={(e) => setAddDescription(e.target.value)}
                          data-testid="ops-fees-add-description"
                        />
                      )}
                    </div>
                  ) : (
                    <Input
                      placeholder="e.g. Jurisdiction site payment – New Castle County"
                      value={addDescription}
                      onChange={(e) => setAddDescription(e.target.value)}
                      data-testid="ops-fees-add-description"
                    />
                  )}
                </div>
              </div>
              <TreatmentPicker
                value={addTreatment}
                onChange={setAddTreatment}
                baseBilling={baseBilling}
                amount={addAmount}
                testid="ops-fees-add-treatment"
              />
              <p className="text-[11px] text-slate-500">
                Note: amounts above {fmtUSD(auditThreshold)} will flag this order for Accounting review.
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowAddForm(false); setAddAmount(''); setAddDescription(''); setAddFeeType(''); setAddTreatment(''); }}
                  disabled={submitting}
                >
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={submitAdd}
                  disabled={submitting}
                  data-testid="ops-fees-add-save"
                >
                  <Save className="w-3 h-3 mr-1" /> Save
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TITLE-FEE-TYPES-003 — Remove confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="ops-fee-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this Operational Fee?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  {fmtUSD(deleteTarget.amount)} — {deleteTarget.description}
                  <br />
                </>
              )}
              This action will be recorded in the audit history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="ops-fee-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="ops-fee-delete-confirm"
            >
              {deleting ? 'Removing…' : 'Remove Fee'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OperationalFeesPanel;
